import { useCallback } from 'react';
import { UserTypeEnum } from 'enums';
import { useAuthUser } from 'features/auth/AuthUserContext';

/**
 * Custom hook to manage access permissions for specific features
 * based on the user type and provided feature settings.
 *
 * @function useJournal
 * @returns {Object} An object containing the `canAccessFeature` function.
 *
 * @property {Function} canAccessFeature - Checks if a specific feature is accessible
 * based on the user's type and feature configuration.
 *
 * @callback canAccessFeature
 * @param {string} appLocale - The application context or locale for the feature (e.g., "workspace", "myCreations").
 * @param {string} feature - The name of the feature to check access for (e.g., "download", "settings").
 * @returns {boolean} Returns `true` if the feature is accessible, otherwise `false`.
 *
 * @example
 * // Example usage
 * const { canAccessFeature } = useJournal();
 * const canDownload = canAccessFeature('workspace', 'download');
 * console.log(canDownload); // true or false
 */
export function useJournal() {
  const { user, userJournalSettings } = useAuthUser();

  const canAccessFeature = useCallback(
    (appLocale, feature) => {
      if (user?.tp_user !== UserTypeEnum.JOURNAL) return true;

      const features = userJournalSettings?.features
        ? JSON.parse(userJournalSettings.features)
        : {};

      return features?.[appLocale]?.[feature] ?? false;
    },
    [user, userJournalSettings],
  );

  return { canAccessFeature };
}
